/**
 * One versioned snapshot of a logical aggregate — a field map, an engagement config, later a
 * contract. Shared because the backend renders every domain through one differ
 * (`framework/versioning`), so the row shape is the same and only the labels differ.
 */

export interface AggregateVersionChange {
  kind: "added" | "removed" | "changed";
  /** Derived path prefix. Groups the drawer, and lets a page filter to its own slice. */
  section: string;
  label: string;
  /** Raw path into the snapshot. Newly-tracked or unlabelled fields show up as this. */
  path: string;
  /**
   * The label split into parts, so the field can be styled apart from the two values — as one
   * string they are typographically identical, and on a long name that is the whole line.
   * `field` with neither value means a whole row appeared or vanished. Absent on domains that
   * have not been split yet, which fall back to `label`.
   */
  field?: string | null;
  old_display?: string | null;
  new_display?: string | null;
  /** The edit is real (the hash moved) but HTML would collapse it into looking identical. */
  whitespace_only: boolean;
  case_only: boolean;
  /** "We started tracking this field", not "this person changed it". */
  system: boolean;
}

export interface AggregateVersionUser {
  name: string;
  email: string;
  /**
   * Staff rather than the customer. Only the ingest file history sets it — it is the one
   * surface both sides act on, so "who" there has to say which side they were on. Absent
   * everywhere else, and absent on the System user, which is a process rather than a person.
   */
  internal?: boolean;
}

export interface AggregateVersion {
  id: number;
  version_number: number;
  created: string;
  change_summary: string;
  is_current: boolean;
  /** Always read with `trigger`: the system user authors the rollout baseline, the schema bumps
   *  and every derived change, and those read completely differently. */
  changed_by: AggregateVersionUser | null;
  trigger: string | null;
  schema_version: number;
  changes: AggregateVersionChange[];
  /**
   * What this version is about, when one timeline covers many things — the ingest file history
   * merges every file in a population, so each row names its own file. Absent on the aggregates
   * whose drawer already has one subject, where it would repeat the subtitle on every row.
   */
  subject?: string | null;
}

/** Triggers every domain can emit. Domains add their own event names on top. */
export const BASE_TRIGGER_LABELS: Record<string, string> = {
  manual_edit: "",
  baseline: "captured at rollout",
  schema_update: "started tracking new fields",
};
